import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSubPartidas } from '@/hooks/useSubPartidas';
import type { TipoCasa } from '@adelante/shared';
import { CircleNotch, Lightning, MagnifyingGlass, PencilSimple, Plus } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { CrearSubPartidaDialog } from './CrearSubPartidaDialog';
import { EditarSubPartidaDialog } from './EditarSubPartidaDialog';

const TIPOS_CASA: TipoCasa[] = ['1N-Techo', '1N-Azotea', '2N-Techo', '2N-Azotea'];
const TODOS = '__todos__';

export function SubPartidasPantalla() {
  const [q, setQ] = useState('');
  const [sprint, setSprint] = useState<string>(TODOS);
  const [tipoCasa, setTipoCasa] = useState<string>(TODOS);
  const [activo, setActivo] = useState<string>('true');
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [creando, setCreando] = useState(false);

  const filtros = {
    ...(q.trim().length >= 2 ? { q: q.trim() } : {}),
    ...(sprint !== TODOS ? { sprint: Number(sprint) } : {}),
    ...(tipoCasa !== TODOS ? { tipo_casa: tipoCasa as TipoCasa } : {}),
    ...(activo === 'true' ? { activo: true } : activo === 'false' ? { activo: false } : {}),
  };

  const query = useSubPartidas(filtros);
  const filas = query.data?.data ?? [];

  const kpis = useMemo(() => {
    const total = filas.length;
    const criticas = filas.filter((r) => r.es_critica).length;
    const sprintsDistintos = new Set(filas.map((r) => r.sprint_numero)).size;
    return { total, criticas, sprintsDistintos };
  }, [filas]);

  // Sprints distintos para el filtro (1..20 típicamente)
  const sprintsOpciones = useMemo(() => {
    const set = new Set<number>();
    for (const f of filas) set.add(f.sprint_numero);
    return Array.from(set).sort((a, b) => a - b);
  }, [filas]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sub-partidas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Catálogo núcleo de ObrasControl — unidades atómicas que conectan sprints con partidas de
            costo.
          </p>
        </div>
        <Button onClick={() => setCreando(true)} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" weight="bold" aria-hidden="true" />
          Nueva sub-partida
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <Label
              htmlFor="filtro-q"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Buscar
            </Label>
            <div className="relative">
              <MagnifyingGlass
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                weight="bold"
                aria-hidden="true"
              />
              <Input
                id="filtro-q"
                type="search"
                placeholder="Código o nombre…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div>
            <Label
              htmlFor="filtro-sprint"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Sprint
            </Label>
            <Select value={sprint} onValueChange={setSprint}>
              <SelectTrigger id="filtro-sprint">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                {sprintsOpciones.map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    Sprint {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="filtro-tc"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Tipo de casa
            </Label>
            <Select value={tipoCasa} onValueChange={setTipoCasa}>
              <SelectTrigger id="filtro-tc">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                {TIPOS_CASA.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-4 flex gap-4 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="activo"
                value="true"
                checked={activo === 'true'}
                onChange={(e) => setActivo(e.target.value)}
              />
              Solo activas
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="activo"
                value="false"
                checked={activo === 'false'}
                onChange={(e) => setActivo(e.target.value)}
              />
              Solo inactivas
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="activo"
                value={TODOS}
                checked={activo === TODOS}
                onChange={(e) => setActivo(e.target.value)}
              />
              Ambas
            </label>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-adelante-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Total filtrado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold tabular-nums">{kpis.total}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Lightning className="h-4 w-4 text-amber-500" weight="bold" aria-hidden="true" />
              Críticas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold tabular-nums">{kpis.criticas}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Sprints distintos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold tabular-nums">{kpis.sprintsDistintos}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla */}
      <Card>
        <CardHeader>
          <CardTitle>Detalle</CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando…
            </div>
          )}
          {query.error && <p className="py-8 text-destructive">Error: {query.error.message}</p>}
          {!query.isLoading && !query.error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-xs">Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Partida</TableHead>
                  <TableHead className="text-center">Sprint</TableHead>
                  <TableHead>Tipos casa</TableHead>
                  <TableHead className="text-center">Crítica</TableHead>
                  <TableHead className="text-center">Activa</TableHead>
                  <TableHead className="w-[60px]" aria-label="Acciones" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      Sin resultados con los filtros actuales.
                    </TableCell>
                  </TableRow>
                )}
                {filas.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setEditandoId(s.id)}
                  >
                    <TableCell className="font-mono text-xs">{s.codigo}</TableCell>
                    <TableCell>
                      <p className="font-medium">{s.nombre}</p>
                      <p className="text-xs text-muted-foreground">{s.grupo_nombre}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="font-mono text-xs text-muted-foreground">
                        {s.partida_codigo}
                      </span>{' '}
                      {s.partida_nombre}
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{s.sprint_numero}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {s.tipos_casa.map((tc) => (
                          <Badge key={tc} variant="outline" className="font-mono text-[10px]">
                            {tc}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {s.es_critica ? (
                        <Lightning
                          className="inline h-4 w-4 text-amber-500"
                          weight="fill"
                          aria-label="Crítica"
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {s.activo ? (
                        <Badge className="bg-adelante-primary text-adelante-foreground hover:bg-adelante-primary-dark">
                          Activa
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactiva</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <PencilSimple
                        className="inline h-4 w-4 text-muted-foreground"
                        weight="bold"
                        aria-hidden="true"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CrearSubPartidaDialog abierto={creando} onClose={() => setCreando(false)} />
      <EditarSubPartidaDialog subPartidaId={editandoId} onClose={() => setEditandoId(null)} />
    </div>
  );
}
